import { Link } from '@/core/i18n/navigation';
import { cn } from '@/lib/utils';
import { LazyVideo } from './lazy-video';

type HeroClip = { video: string; poster?: string; label: string };

// Fan of three clips: middle one forward, sides tilted back.
const FAN = [
  'translate-y-6 -rotate-6 opacity-90',
  'z-10 scale-105 shadow-2xl',
  'translate-y-6 rotate-6 opacity-90',
];

/**
 * Generator page hero: breadcrumb, trend badge, H1, subtitle and, when
 * the tool has example clips, a fanned stack of them beside the copy.
 * `overlap` leaves room at the bottom for the studio panel to sit over
 * the hero's edge.
 */
export function ToolHero({
  swatch,
  breadcrumb,
  eyebrow,
  title,
  subtitle,
  examples,
  updatedLabel,
  overlap = false,
}: {
  swatch: string;
  breadcrumb: { parentLabel: string; parentHref: string; current: string };
  eyebrow: string;
  title: string;
  subtitle: string;
  examples: readonly HeroClip[];
  updatedLabel: string;
  overlap?: boolean;
}) {
  const clips = examples.slice(0, 3);
  const split = clips.length === 3;

  return (
    <header
      className={cn(
        'relative overflow-hidden rounded-2xl px-6 pt-10 text-white sm:px-12 sm:pt-14',
        overlap ? 'pb-28 sm:pb-36' : 'pb-10 sm:pb-14',
      )}
      style={{ background: swatch }}
    >
      <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-black/20 to-black/60" aria-hidden="true" />
      <div className="absolute -right-24 -top-24 size-72 rounded-full bg-white/10 blur-3xl" aria-hidden="true" />
      <div
        className={cn(
          'relative mx-auto grid items-center gap-12',
          split ? 'max-w-6xl lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]' : 'max-w-3xl',
        )}
      >
        <div className={cn('space-y-5', split ? 'text-center lg:text-left' : 'text-center')}>
          <nav aria-label="Breadcrumb" className="text-xs font-medium text-white/75">
            <Link href={breadcrumb.parentHref} className="hover:text-white hover:underline">
              {breadcrumb.parentLabel}
            </Link>
            <span className="px-1.5" aria-hidden="true">/</span>
            <span>{breadcrumb.current}</span>
          </nav>
          <span className="inline-flex rounded-full bg-white/95 px-3.5 py-1 text-xs font-bold uppercase tracking-[0.12em] text-neutral-900 shadow-sm">
            {eyebrow}
          </span>
          <h1 className="text-balance text-4xl font-black leading-[1.05] tracking-tight sm:text-6xl">{title}</h1>
          <p
            className={cn(
              'max-w-2xl text-pretty text-base leading-7 text-white/90 sm:text-lg sm:leading-8',
              split ? 'mx-auto lg:mx-0' : 'mx-auto',
            )}
          >
            {subtitle}
          </p>
          <p className="text-xs text-white/70">{updatedLabel}</p>
        </div>

        {split ? (
          <div className="mx-auto hidden w-full max-w-md items-start justify-center gap-4 pb-6 sm:flex">
            {clips.map((clip, index) => (
              <LazyVideo
                key={clip.video}
                src={clip.video}
                poster={clip.poster}
                label={clip.label}
                className={cn('aspect-[9/16] w-1/3 rounded-2xl ring-2 ring-white/80 transition-transform', FAN[index])}
              />
            ))}
          </div>
        ) : null}
      </div>
    </header>
  );
}
